import {FactRequest} from '@webmate-web-core/shared/types/testmgmt';
import {Browser} from '@webmate-web-core/shared/types/browser';

export const WORKBENCH_QUALITY_SENSE_FEATURE_ID = 'webmate.qualitysense';

interface QualitySenseFactRequest<T extends string, P> extends FactRequest {
    factType: {findingType: T};
    params: {value: P};
}

type WorkbenchQualitySenseFactRequest =
    QualitySenseFactRequest<'Network.HttpErrorResponse', {minimumStatus: number}> |
    QualitySenseFactRequest<'Javascript.ConsoleMessage', {levels: string[], messageRegex?: string}>;

export interface QualitySensePolicy {
    name: string;
    requestedFacts: WorkbenchQualitySenseFactRequest[];
}

/**
 * Built-in first policy. The Workbench resolves it to FactRequests when it creates an Expedition;
 * it is not a persisted or independently versioned policy yet.
 */
export const WORKBENCH_QUALITY_SENSE_POLICY: QualitySensePolicy = {
    name: 'Frontend health',
    requestedFacts: [
        {
            factType: {findingType: 'Network.HttpErrorResponse'},
            params: {
                value: {
                    minimumStatus: 400
                }
            }
        },
        {
            factType: {findingType: 'Javascript.ConsoleMessage'},
            params: {
                value: {
                    levels: ['error']
                }
            }
        }
    ]
};

export function supportsWorkbenchQualitySense(browser: Browser, cdpAvailable: boolean): boolean {
    const seleniumVersion = browser.properties?.selenium?.version;
    return cdpAvailable &&
        !browser.isMobile() &&
        ['CHROME', 'EDGE'].includes(browser.browserType.toUpperCase()) &&
        typeof seleniumVersion === 'string' && seleniumVersion.startsWith('4');
}

/**
 * Device-backed monitoring requires a supported Chromium browser controlled through Selenium 4 and an advertised CDP endpoint.
 * Unsupported combinations continue to start normally, without this CDP-backed policy.
 */
export function qualitySenseFactRequestsForBrowser(browser: Browser, cdpAvailable: boolean): FactRequest[] {
    return supportsWorkbenchQualitySense(browser, cdpAvailable)
        ? WORKBENCH_QUALITY_SENSE_POLICY.requestedFacts
        : [];
}
